import { supabase } from '../lib/supabase';

// Helper: Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Helper: Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Get today's analytics
export const getTodaysAnalytics = async () => {
  try {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const { data, error } = await supabase?.from('analytics')?.select('*')?.eq('date', today)?.maybeSingle();

    if (error) throw error;
    
    if (!data) {
      // Create initial analytics for today
      const { data: newData, error: insertError } = await supabase?.from('analytics')?.insert({
          total_visits: 0,
          daily_interactions: 0,
          content_type_stats: {},
          date: today
        })?.select()?.single();

      if (insertError) throw insertError;
      return { data: toCamelCase(newData), error: null };
    }

    return { data: toCamelCase(data), error: null };
  } catch (error) {
    console.error('Error fetching today analytics:', error);
    return { data: null, error };
  }
};

// Update analytics
export const updateAnalytics = async (analyticsData) => {
  try {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    const snakeData = toSnakeCase(analyticsData);
    
    const { data, error } = await supabase?.from('analytics')?.upsert(
        {
          ...snakeData,
          date: today,
          last_updated: new Date()?.toISOString()
        },
        { onConflict: 'date' }
      )?.select()?.single();

    if (error) throw error;
    return { data: toCamelCase(data), error: null };
  } catch (error) {
    console.error('Error updating analytics:', error);
    return { data: null, error };
  }
};

// Increment visit count
export const incrementVisitCount = async () => {
  try {
    const { data: current } = await getTodaysAnalytics();
    
    if (current) {
      const updated = {
        ...current,
        totalVisits: (current?.totalVisits || 0) + 1
      };
      return await updateAnalytics(updated);
    }
    
    return { data: null, error: { message: 'Failed to get current analytics' } };
  } catch (error) {
    console.error('Error incrementing visit count:', error);
    return { data: null, error };
  }
};

// Track interaction
export const trackInteraction = async (contentType) => {
  try {
    const { data: current } = await getTodaysAnalytics();
    
    if (current) {
      const contentTypeStats = current?.contentTypeStats || {};
      const currentCount = contentTypeStats?.[contentType] || 0;
      
      const updated = {
        ...current,
        dailyInteractions: (current?.dailyInteractions || 0) + 1,
        contentTypeStats: {
          ...contentTypeStats,
          [contentType]: currentCount + 1
        }
      };
      
      return await updateAnalytics(updated);
    }
    
    return { data: null, error: { message: 'Failed to get current analytics' } };
  } catch (error) {
    console.error('Error tracking interaction:', error);
    return { data: null, error };
  }
};

// Get all analytics (for admin dashboard)
export const getAllAnalytics = async () => {
  try {
    const { data, error } = await supabase?.from('analytics')?.select('*')?.order('date', { ascending: false })?.limit(30);

    if (error) throw error;
    return { data: data?.map(toCamelCase), error: null };
  } catch (error) {
    console.error('Error fetching all analytics:', error);
    return { data: [], error };
  }
};

// Log Instagram Stories share action
export const logInstagramShare = async ({ contentType, instagramUsername }) => {
  try {
    const { data, error } = await supabase?.from('instagram_shares')?.insert({
      content_type: contentType || 'dailyMessage',
      instagram_username: instagramUsername || null,
      shared_at: new Date()?.toISOString()
    })?.select()?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error logging Instagram share:', error);
    return { data: null, error };
  }
};

// Get monthly visit counts (QR scans per month)
export const getMonthlyVisits = async () => {
  try {
    const { data, error } = await supabase
      ?.from('analytics')
      ?.select('date, total_visits')
      ?.order('date', { ascending: true });

    if (error) throw error;

    // Group by year-month and sum total_visits
    const monthlyMap = {};
    (data || [])?.forEach((row) => {
      if (!row?.date) return;
      const monthKey = row?.date?.substring(0, 7); // "YYYY-MM"
      monthlyMap[monthKey] = (monthlyMap?.[monthKey] || 0) + (row?.total_visits || 0);
    });

    const months = Object.entries(monthlyMap)?.map(([month, count]) => ({
      month,
      count
    }));

    return { data: months, error: null };
  } catch (error) {
    console.error('Error fetching monthly visits:', error);
    return { data: [], error };
  }
};